import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Phone, MapPin, Calendar, Package, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_LABELS = {
  domestic_borewell: "Home Borewell",
  booster: "Pressure Booster",
  agriculture: "Agriculture",
  sump_lift: "Sump Lift",
  openwell: "Open Well",
  sewage: "Sewage",
  dewatering: "Dewatering",
};

export default function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leads`);
      setLeads(r.data.leads || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchLeads(); }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Admin</h1>
          <button onClick={fetchLeads} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 mb-5 border-b border-gray-200">
          <div className="px-4 py-2.5 text-sm font-semibold text-[#B22222] border-b-2 border-[#B22222] -mb-[1px]">
            Leads ({leads.length})
          </div>
          <Link
            to="/admin/catalog"
            className="px-4 py-2.5 text-sm font-medium text-gray-500 hover:text-[#B22222]"
          >
            Catalog
          </Link>
        </div>

        {loading && leads.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : leads.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No leads yet</div>
        ) : (
          <div className="space-y-3">
            {leads.map((lead) => {
              const rec = lead.recommendation || {};
              const pump = rec.primary || {};
              const reqs = lead.requirements || {};
              const isOpen = expanded[lead.id];

              return (
                <div key={lead.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Header - always visible */}
                  <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={() => toggle(lead.id)}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{lead.ref_number}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 font-medium">
                            {INTENT_LABELS[lead.intent] || lead.intent}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900">{lead.name || "No name"}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1"><Phone size={14} /> {lead.phone}</span>
                          {lead.location && <span className="flex items-center gap-1"><MapPin size={14} /> {lead.location}</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        {pump.name ? (
                          <div className="text-sm">
                            <div className="font-semibold text-gray-900">{pump.name}</div>
                            <div className="text-gray-500">{pump.hp}HP · ₹{(pump.price || 0).toLocaleString()}</div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No recommendation</span>
                        )}
                        <div className="mt-2">
                          {isOpen ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isOpen && (
                    <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50">
                      <div className="grid sm:grid-cols-2 gap-4">
                        {/* Requirements */}
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Requirements</h4>
                          <div className="space-y-1 text-sm">
                            {Object.entries(reqs).map(([k, v]) => (
                              <div key={k} className="flex justify-between">
                                <span className="text-gray-500">{k.replace(/_/g, " ")}</span>
                                <span className="text-gray-800 font-medium">{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Recommendation */}
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Recommendation</h4>
                          {pump.name ? (
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <div className="font-semibold text-gray-900">{pump.brand} {pump.name}</div>
                              <div className="text-sm text-gray-600 mt-1">
                                {pump.hp} HP · {pump.phase} Phase
                                {pump.head_max && ` · ${pump.head_max}ft head`}
                                {pump.max_showers && ` · ${pump.max_showers} showers`}
                              </div>
                              {pump.price && <div className="text-lg font-bold text-teal-600 mt-2">₹{pump.price.toLocaleString()}</div>}
                              {rec.why && rec.why.length > 0 && (
                                <ul className="mt-2 text-xs text-gray-500 space-y-1">
                                  {rec.why.slice(0, 2).map((w, i) => <li key={i}>• {w}</li>)}
                                </ul>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400">Not captured</div>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-400">
                        <span className="flex items-center gap-1"><Calendar size={12} /> {formatDate(lead.created_at)}</span>
                        <a href={`https://wa.me/${lead.phone}`} target="_blank" rel="noreferrer" className="text-green-600 hover:underline">
                          WhatsApp →
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
